import {
  BarChartRounded,
  CompareRounded,
  DirectionsBoatRounded,
  GroupRounded,
  QrCodeRounded,
  ReceiptLongRounded,
  ShareLocationRounded,
  StorefrontRounded,
  WhereToVoteRounded,
} from '@mui/icons-material';
import { Box, Button, Stack, styled, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { get, isUndefined } from 'lodash';
import { SyntheticEvent, useMemo } from 'react';
import { Calendar, Sliders } from 'react-feather';
import { Link, useSearchParams } from 'react-router-dom';
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import MainContainer from 'src/components/common/MainContainer';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import TabComponent from 'src/components/Tab/TabComponent';
import Analytics from 'src/features/CompanyTradeData/Analytics';
import CompanyTradeDataBOL from 'src/features/CompanyTradeData/BOL';
import CompanyTradeDataBuyerSupplier from 'src/features/CompanyTradeData/BuyerSupplier';
import CompanyTradeDataCompetitor from 'src/features/CompanyTradeData/Competitor';
import CompanyTradeDataHSCode from 'src/features/CompanyTradeData/HSCode';
import CompanyTradeDataTradingArea from 'src/features/CompanyTradeData/TradingArea';
import CompanyTradeDataViewByPort from 'src/features/CompanyTradeData/ViewByPort';
import { useQueryParsedParams } from 'src/hooks/useQueryParam';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { TypeCompanyTradeDataSearch } from 'src/utils/constants/enum';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export enum CompanyTradeDataTab {
  Analytics = 'Analytics',
  BillsOfLading = 'BillsOfLading',
  HSCode = 'HSCode',
  Buyers = 'Buyers',
  Suppliers = 'Suppliers',
  PortsOfDelivery = 'PortsOfDelivery',
  PortsOfOrigin = 'PortsOfOrigin',
  TradingArea = 'TradingArea',
  Competitors = 'Competitors',
}

const CompanyTradeData = () => {
  const { start_date, end_date, keyword, tab, country_code, is_buyer, company_type } = useQueryParsedParams();
  const [, setSearchParams] = useSearchParams();
  const { handleOpenSearch } = useFormSearch((state) => state);
  const selectedTab = useMemo(() => {
    return tab || tabs[0].key;
  }, [tab]);
  const {
    palette: { common },
  } = useTheme();

  const content = useMemo(() => {
    return {
      [CompanyTradeDataTab.Analytics]: <Analytics />,
      [CompanyTradeDataTab.BillsOfLading]: <CompanyTradeDataBOL />,
      [CompanyTradeDataTab.HSCode]: <CompanyTradeDataHSCode />,
      [CompanyTradeDataTab.Buyers]: <CompanyTradeDataBuyerSupplier />,
      [CompanyTradeDataTab.Suppliers]: <CompanyTradeDataBuyerSupplier />,
      [CompanyTradeDataTab.PortsOfDelivery]: <CompanyTradeDataViewByPort />,
      [CompanyTradeDataTab.PortsOfOrigin]: <CompanyTradeDataViewByPort />,
      [CompanyTradeDataTab.TradingArea]: <CompanyTradeDataTradingArea />,
      [CompanyTradeDataTab.Competitors]: <CompanyTradeDataCompetitor />,
    } as Record<CompanyTradeDataTab, React.ReactNode>;
  }, []);

  const tabs = useMemo(() => {
    return [
      {
        key: CompanyTradeDataTab.Analytics,
        label: 'Market Analytics',
        icon: BarChartRounded,
        disable: false,
      },
      {
        key: CompanyTradeDataTab.Buyers,
        label: 'Buyers',
        icon: GroupRounded,
        disable: false,
        display: company_type === TypeCompanyTradeDataSearch.BOTH || !is_buyer,
      },
      {
        key: CompanyTradeDataTab.Suppliers,
        label: 'Suppliers',
        icon: StorefrontRounded,
        disable: false,
        display: company_type === TypeCompanyTradeDataSearch.BOTH || is_buyer,
      },
      {
        key: CompanyTradeDataTab.HSCode,
        label: 'HS Code',
        icon: QrCodeRounded,
        disable: false,
      },
      {
        key: CompanyTradeDataTab.Competitors,
        label: 'Competitors',
        icon: CompareRounded,
        disable: false,
        display: false,
      },
      {
        key: CompanyTradeDataTab.PortsOfDelivery,
        label: 'Ports of Delivery',
        icon: DirectionsBoatRounded,
        disable: false,
      },
      {
        key: CompanyTradeDataTab.PortsOfOrigin,
        label: 'Ports of Origin',
        icon: WhereToVoteRounded,
        disable: false,
      },
      {
        key: CompanyTradeDataTab.TradingArea,
        label: 'Trading Area',
        icon: ShareLocationRounded,
        disable: false,
      },
      {
        key: CompanyTradeDataTab.BillsOfLading,
        label: 'Bills of Lading',
        icon: ReceiptLongRounded,
        disable: false,
      },
    ].filter(({ display }) => isUndefined(display) || display);
  }, [is_buyer, company_type]);

  const selectedContent = useMemo(() => get(content, selectedTab, null), [content, selectedTab]);

  return (
    <MainContainer maxWidth="xl">
      <Stack spacing={2}>
        <BreadcrumbWithBtnBack
          listItem={[
            {
              text: 'Company Trade Data',
            },
            {
              text: keyword,
            },
          ]}
        />
        <StyledBox sx={{ color: common.white }}>
          <Typography color={'inherit'} variant="h4">
            Search results for "{keyword}
            {country_code && (
              <Box display={'inline'} sx={{ ml: 1 }}>
                <FlagCountry width={40} code={country_code} />
              </Box>
            )}
            " as{' '}
            {company_type === TypeCompanyTradeDataSearch.BOTH
              ? 'suppliers or buyers'
              : is_buyer
                ? 'buyers'
                : 'suppliers'}
          </Typography>
          <Stack spacing={1} direction="row" alignItems="center">
            <IconFeather icon={<Calendar />} muiProps={{ sx: { color: common.white, fontSize: 16 } }} />
            <Typography color={'inherit'} variant="caption" align="left">
              {dayjs(start_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}
            </Typography>
            <Typography color={'inherit'} variant="caption" align="left">
              -
            </Typography>
            <IconFeather icon={<Calendar />} muiProps={{ sx: { color: common.white, fontSize: 16 } }} />
            <Typography color={'inherit'} variant="caption" align="left">
              {dayjs(end_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}
            </Typography>
          </Stack>
        </StyledBox>

        <TabComponent
          tabs={tabs}
          selected={selectedTab}
          handleChange={(_: SyntheticEvent, newValue: string) => {
            setSearchParams((prev) => {
              prev.set('tab', newValue);
              return prev;
            });
          }}
        />
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            onClick={() => {
              handleOpenSearch(FormType.SearchTradeData);
            }}
            color="primary"
            startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Sliders />} />}
            variant="outlined"
          >
            Refine your search
          </Button>
        </Box>
        <SectionErrorBoundary resetKey={selectedTab} sectionName={`CompanyTrade - ${selectedTab}`}>
          {selectedContent}
        </SectionErrorBoundary>
      </Stack>
    </MainContainer>
  );
};

export default CompanyTradeData;
